/**
 * useHapticFeedback Hook
 *
 * Provides haptic feedback (vibration) on supported mobile devices.
 * Uses the Vibration API with fallback for unsupported devices.
 */

type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

// Vibration patterns in milliseconds
const HAPTIC_PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 10], // Short-pause-short
  warning: [25, 30, 25], // Medium-pause-medium
  error: [50, 100, 50, 100, 50], // Long-pause-long-pause-long
};

/**
 * Check if the Vibration API is supported
 */
function isVibrationSupported(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

/**
 * Trigger haptic feedback with the specified pattern
 */
function triggerHaptic(pattern: HapticPattern = 'light'): void {
  if (!isVibrationSupported()) return;

  try {
    navigator.vibrate(HAPTIC_PATTERNS[pattern]);
  } catch {
    // Silently fail if vibration is blocked or fails
    // (no logging needed for production)
  }
}

/**
 * Hook for haptic feedback
 *
 * @returns Object with haptic trigger functions
 *
 * @example
 * const { light, success, error } = useHapticFeedback();
 * onClick={() => { light(); handleClick(); }}
 */
export function useHapticFeedback() {
  return {
    /** Light tap feedback (10ms) - for button presses */
    light: () => triggerHaptic('light'),

    /** Medium feedback (25ms) - for selections */
    medium: () => triggerHaptic('medium'),

    /** Heavy feedback (50ms) - for important actions */
    heavy: () => triggerHaptic('heavy'),

    /** Success pattern - for confirmations */
    success: () => triggerHaptic('success'),

    /** Warning pattern - for caution states */
    warning: () => triggerHaptic('warning'),

    /** Error pattern - for error feedback */
    error: () => triggerHaptic('error'),

    /** Check if haptic feedback is available */
    isSupported: isVibrationSupported(),
  };
}
